import React, { useCallback } from 'react';
import { useIntl, defineMessage } from 'react-intl';
import classnames from 'clsx';
import { get } from 'lodash-es';

import { Button } from 'ui-components/components/button';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { noop } from 'ui-components/default-props/noop';

import style from './seller-provider-list-item.module.less';
import rateShopperIcon from '../../../../pages/home-page/version2/svgs/rate-shopper-icon.svg';
import { DataDrivenLogo } from '../../../data-driven-logo';

const rateShopperAltText = defineMessage({
  defaultMessage: 'Rate Shopper Truck Shopping Rates',
  description: 'Rate Browser Rate Shopper Icon',
  id: 'bQOjxP',
});

/**
 * Renders a single seller provider item in the provider list.
 * @param {object} props
 * @param {number} [props.providerId] - The label provider ID for displaying the logo
 * @param {string} [props.name] - The provider name to display
 * @param {number} [props.numberOfValidRates] - Count of valid rates for this provider
 * @param {boolean} [props.selected] - Whether this provider is currently selected
 * @param {boolean} [props.isLoading] - Whether rates are still loading for this provider
 * @param {boolean} [props.isServiceCollection] - Whether this is a service collection (rate shopper)
 * @param {(id: string) => void} [props.onClick] - Callback when the provider is clicked
 * @param {string} [props.className] - Additional CSS class name
 * @param {string} props.sellerLabelProviderId - The seller label provider ID
 * @returns {JSX.Element}
 */
export const SellerProviderListItem = ({
  providerId = 0,
  name = '',
  numberOfValidRates = 0,
  selected = false,
  isLoading = false,
  isServiceCollection = false,
  onClick = noop,
  className = '',
  sellerLabelProviderId,
}) => {
  const intl = useIntl();

  const topLevelClassName = classnames(style['seller-provider-list-item'], className, {
    [style['has-rates']]: numberOfValidRates > 0,
    [style.selected]: selected,
  });
  const shouldTruncateName = get(name, 'length', 0) > 24;
  const truncatedName = shouldTruncateName ? name.substr(0, 24) : name;

  const selectProvider = useCallback(() => {
    onClick(sellerLabelProviderId);
  }, [onClick, sellerLabelProviderId]);

  return (
    <Button className={topLevelClassName} onClick={selectProvider} theme='unstyled'>
      <div className={style['logo-wrapper']}>
        {isServiceCollection ? (
          <img alt={intl.formatMessage(rateShopperAltText)} src={rateShopperIcon} />
        ) : (
          <DataDrivenLogo logoType='icon' providerId={providerId} />
        )}
      </div>
      <div className={style.name}>
        {truncatedName}
        {shouldTruncateName ? <span>&hellip;</span> : null}
      </div>
      {!isLoading ? <div className={style.count}>{numberOfValidRates}</div> : null}
      {isLoading ? (
        <div className={style['loading-indicator-wrapper']}>
          <LoadingIndicator className={style['loading-indicator']} />
        </div>
      ) : null}
    </Button>
  );
};
